import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useAuth } from "react-oidc-context";
import { useQueryClient } from "@tanstack/react-query";
import { listOutbox, onOutboxChange, removeOutbox, updateOutbox, type OutboxItem } from "../lib/outbox";
import { syncOutbox, type SyncResult } from "../lib/sync";

interface OutboxState {
  items: OutboxItem[];
  pending: OutboxItem[];
  failed: OutboxItem[];
  online: boolean;
  syncing: boolean;
  last?: SyncResult;
  syncNow: () => void;
  retry: (id: string) => Promise<void>;
  discard: (id: string) => Promise<void>;
}

const Ctx = createContext<OutboxState | null>(null);

/**
 * Keeps the offline outbox moving: replays it when the app starts, when the
 * connection returns, when the tab becomes visible, when something is added,
 * and every minute as a safety net. Mounted once at the root.
 */
export function OutboxProvider({ children }: { children: ReactNode }) {
  const auth = useAuth();
  const qc = useQueryClient();
  const token = auth.user?.access_token;
  const [items, setItems] = useState<OutboxItem[]>([]);
  const [online, setOnline] = useState(typeof navigator === "undefined" ? true : navigator.onLine);
  const [syncing, setSyncing] = useState(false);
  const [last, setLast] = useState<SyncResult | undefined>();
  const tokenRef = useRef(token);
  tokenRef.current = token;

  const reload = useCallback(() => {
    listOutbox()
      .then(setItems)
      .catch(() => setItems([]));
  }, []);

  const syncNow = useCallback(() => {
    const t = tokenRef.current;
    if (!t || !navigator.onLine) return;
    setSyncing(true);
    syncOutbox(t)
      .then((r) => {
        setLast(r);
        if (r.sent > 0) void qc.invalidateQueries();
      })
      .catch(() => undefined)
      .finally(() => setSyncing(false));
  }, [qc]);

  useEffect(() => {
    reload();
    return onOutboxChange(reload);
  }, [reload]);

  useEffect(() => {
    const up = () => {
      setOnline(true);
      syncNow();
    };
    const down = () => setOnline(false);
    const visible = () => {
      if (document.visibilityState === "visible") syncNow();
    };
    window.addEventListener("online", up);
    window.addEventListener("offline", down);
    document.addEventListener("visibilitychange", visible);
    const timer = window.setInterval(syncNow, 60_000);
    return () => {
      window.removeEventListener("online", up);
      window.removeEventListener("offline", down);
      document.removeEventListener("visibilitychange", visible);
      window.clearInterval(timer);
    };
  }, [syncNow]);

  // New token (sign-in or silent renew) or new pending items: try right away.
  const pendingCount = items.filter((i) => i.status === "pending").length;
  useEffect(() => {
    if (token && pendingCount > 0) syncNow();
  }, [token, pendingCount, syncNow]);

  const value = useMemo<OutboxState>(
    () => ({
      items,
      pending: items.filter((i) => i.status === "pending"),
      failed: items.filter((i) => i.status === "failed"),
      online,
      syncing,
      last,
      syncNow,
      retry: async (id) => {
        await updateOutbox(id, { status: "pending", attempts: 0, lastError: undefined });
        syncNow();
      },
      discard: (id) => removeOutbox(id),
    }),
    [items, online, syncing, last, syncNow],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useOutbox(): OutboxState {
  const v = useContext(Ctx);
  if (!v) throw new Error("useOutbox must be used inside OutboxProvider");
  return v;
}
